import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Spin, Card, Checkbox, message, Modal, Tooltip, Divider } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, IdcardOutlined, DatabaseOutlined, PhoneOutlined, FacebookOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import './Login.css';

function Signup() {
  const [loading, setLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const navigate = useNavigate();

  const onFinish = async (values) => {
    const payload = {
      fullName: values.fullName,
      email: values.email,
      password: values.password,
      phoneNumber: values.phoneNumber,
      smsConsent: values.smsConsent || false,
      geotab: {
        userName: values.geotabUserName,
        password: values.geotabPassword,
        database: values.geotabDatabase,
      }
    };

    // Log JSON as requested
    // eslint-disable-next-line no-console
    console.log(JSON.stringify(payload, null, 2));

    setLoading(true);
    try {
      const res = await api.post('/auth/signup', payload);
      if (res && res.token && res.user) {
        localStorage.setItem('token', res.token);
        localStorage.setItem('user', JSON.stringify(res.user));
        message.success('Account created successfully');
        // Navigate to dashboard based on role (client by default from backend)
        navigate('/dashboard/client', { replace: true });
      } else {
        message.error('Unexpected response from server');
      }
    } catch (err) {
      message.error(err?.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLogin = () => {
    setFacebookLoading(true);
    // Show permission modal first
    setShowPermissionModal(true);
    setFacebookLoading(false);
  };

  const handleFacebookPermissionAccept = async () => {
    setFacebookLoading(true);
    try {
      // Initiate Facebook OAuth flow
      const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID || '2130322127499145';
      
      // Determine backend URL (same as FacebookManager)
      const backendUrl = (() => {
        const hostname = window.location.hostname;
        if (hostname === 'app.pathmarkr.com' || hostname === 'pathmarkr.com' || hostname.includes('amazonaws.com')) {
          return 'https://api.pathmarkr.com';
        }
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          return 'http://localhost:5000';
        }
        return 'https://api.pathmarkr.com';
      })();
      
      const redirectUri = `${backendUrl}/api/integrations/meta/callback`;
      const scope = 'ads_management,ads_read';
      
      // Store that user is in signup flow
      localStorage.setItem('facebookSignupFlow', 'true');
      
      // Redirect to Facebook OAuth
      const authUrl = `https://www.facebook.com/v18.0/dialog/oauth?` +
        `client_id=${facebookAppId}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=code&` +
        `state=signup_${Date.now()}`;
      
      window.location.href = authUrl;
    } catch (error) {
      message.error('Failed to connect to Facebook');
      setFacebookLoading(false);
    }
  };

  const handleFacebookPermissionReject = () => {
    setShowPermissionModal(false);
    message.info('Facebook connection is optional for enhanced features');
  };

  return (
    <div className="login-container">
      <div className="login-surface">
        <section className="login-hero">
          <div className="login-hero-content">
            <span className="login-badge">Pathmarkr</span>
            <h1 className="login-hero-title">Create your Pathmarkr account</h1>
            <p className="login-hero-subtitle">Ad automation platform using fleet GPS data for hyper-local Facebook campaigns.</p>
          </div>
        </section>

        <section className="login-card">
          <div className="login-header">Create account</div>
          <div className="login-subheader">All fields are required.</div>

          <Spin spinning={loading}>
            <Form name="signup" onFinish={onFinish} className="login-form" layout="vertical">
              <Form.Item
                name="fullName"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter your full name' }]}
              >
                <Input prefix={<IdcardOutlined className="login-icon" />} placeholder="Full Name" size="large" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input prefix={<MailOutlined className="login-icon" />} placeholder="Email address" size="large" />
              </Form.Item>

              <Form.Item
                name="phoneNumber"
                label="Phone Number"
                rules={[
                  { required: true, message: 'Please enter your phone number' },
                  { pattern: /^[0-9\s\-()+ ]+$/, message: 'Please enter a valid phone number' },
                ]}
              >
                <Input prefix={<PhoneOutlined className="login-icon" />} placeholder="Enter your phone number here" size="large" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter a password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                ]}
              >
                <Input.Password prefix={<LockOutlined className="login-icon" />} placeholder="Password" size="large" />
              </Form.Item>

              <Form.Item
                name="smsConsent"
                valuePropName="checked"
                rules={[{ required: true, message: 'You must agree to receive SMS messages to continue.' }]}
                style={{ marginBottom: 16, padding: 16, backgroundColor: '#1f1f1f', borderRadius: 8, border: '1px solid #333' }}
              >
                <Checkbox>
                  <span style={{ fontSize: 14, color: '#fff', fontWeight: 400, lineHeight: 1.5 }}>
                    I agree to receive SMS messages from PathMarkr related to account notifications, promotions, and support updates. Message frequency may vary. Message & data rates may apply. Reply STOP to opt out or HELP for help. View our{' '}
                    <a 
                      href="https://pathmarkr.com/terms-of-service" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ color: '#1890ff', textDecoration: 'none' }}
                    >
                      Terms
                    </a>
                    {' '}and{' '}
                    <a 
                      href="https://pathmarkr.com/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ color: '#1890ff', textDecoration: 'none' }}
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </Checkbox>
              </Form.Item>

              <div style={{ fontSize: 12, color: '#999', marginBottom: 16, textAlign: 'center', fontStyle: 'italic' }}>
                Consent is not a condition of purchase.
              </div>

              <Card
                title="GeoTab Account Details"
                size="small"
                style={{ borderRadius: 12, marginTop: 8, marginBottom: 8 }}
                headStyle={{ fontWeight: 600 }}
              >
                <Form.Item
                  name="geotabUserName"
                  label="GeoTab Username"
                  rules={[{ required: true, message: 'Please enter your GeoTab username' }]}
                >
                  <Input prefix={<UserOutlined className="login-icon" />} placeholder="GeoTab username" size="large" />
                </Form.Item>

                <Form.Item
                  name="geotabPassword"
                  label="GeoTab Password"
                  rules={[{ required: true, message: 'Please enter your GeoTab password' }]}
                >
                  <Input.Password prefix={<LockOutlined className="login-icon" />} placeholder="GeoTab password" size="large" />
                </Form.Item>

                <Form.Item
                  name="geotabDatabase"
                  label="GeoTab Database"
                  rules={[{ required: true, message: 'Please enter your GeoTab database' }]}
                >
                  <Input prefix={<DatabaseOutlined className="login-icon" />} placeholder="GeoTab database" size="large" />
                </Form.Item>
              </Card>

              <Divider style={{ margin: '24px 0' }}>
                <span style={{ color: '#999', fontSize: 14 }}>OR</span>
              </Divider>

              <Card
                title="Connect Facebook for Enhanced Features"
                size="small"
                style={{ borderRadius: 12, marginBottom: 16 }}
                headStyle={{ fontWeight: 600 }}
              >
                <div style={{ marginBottom: 16 }}>
                  <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
                    Connect your Facebook account to:
                  </p>
                  <ul style={{ margin: '8px 0', paddingLeft: 20, fontSize: 13, color: '#666' }}>
                    <li>Create and manage Facebook ad campaigns</li>
                    <li>Read campaign performance metrics and insights</li>
                    <li>Publish campaigns directly to Meta platforms</li>
                  </ul>
                </div>

                <Button
                  icon={<FacebookOutlined />}
                  onClick={handleFacebookLogin}
                  loading={facebookLoading}
                  size="large"
                  block
                  style={{
                    backgroundColor: '#1877f2',
                    borderColor: '#1877f2',
                    color: 'white',
                    fontWeight: 500,
                    height: 40
                  }}
                >
                  Connect Facebook Account
                </Button>

                <div style={{ marginTop: 12, fontSize: 12, color: '#999', textAlign: 'center' }}>
                  <Tooltip title="Facebook connection is optional but recommended for full campaign features">
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    Optional - You can connect later in settings
                  </Tooltip>
                </div>
              </Card>

              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" className="login-button" block>
                  Sign Up
                </Button>
              </Form.Item>

              <div className="login-links">
                <span>Already have an account?</span>
                <span className="login-divider">•</span>
                <Link to="/login" className="login-link">Sign in</Link>
              </div>

              <div style={{ fontSize: 12, color: '#999', textAlign: 'center', marginTop: 16 }}>
                By signing up you agree to our{' '}
                <a href="https://pathmarkr.com/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="https://pathmarkr.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>Privacy Policy</a>.
              </div>
            </Form>
          </Spin>
        </section>
      </div>

      {/* Facebook Permission Request Modal */}
      <Modal
        title="Facebook Permissions Required"
        open={showPermissionModal}
        onCancel={handleFacebookPermissionReject}
        footer={[
          <Button key="reject" onClick={handleFacebookPermissionReject}>
            Skip for Now
          </Button>,
          <Button key="accept" type="primary" onClick={handleFacebookPermissionAccept}>
            Accept & Connect
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ color: '#1877f2', marginBottom: 8 }}>Pathmarkr is an ad automation platform that uses fleet GPS data to create hyper-local ad targeting zones.</h4>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>To connect your Facebook Ads account, we need permission to:</p>
          
          <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <strong><Tooltip title="Manage Facebook ads">ads_management</Tooltip></strong>
              <p style={{ margin: '4px 0', fontSize: 13, color: '#666' }}>
                Create and manage Facebook advertising campaigns using GPS-triggered targeting zones
              </p>
            </div>

            <div style={{ marginBottom: 12 }}>
              <strong><Tooltip title="Read ad performance data">ads_read</Tooltip></strong>
              <p style={{ margin: '4px 0', fontSize: 13, color: '#666' }}>
                Access campaign performance metrics and insights for optimization
              </p>
            </div>

            <div style={{ marginBottom: 12, padding: 12, background: '#e8f4fd', borderRadius: 6, border: '1px solid #bae7ff' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#0050b3' }}>
                <strong>Note:</strong> Basic profile information and email access are automatically granted for account identification.
              </p>
            </div>
          </div>

          <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
            <p><strong>Privacy & Security:</strong></p>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              <li>We only access data necessary for campaign management</li>
              <li>Your information is encrypted and securely stored</li>
              <li>You can revoke permissions at any time</li>
              <li>We never share your data with third parties</li>
            </ul>
          </div>

          <div style={{ marginTop: 16, padding: 12, background: '#fff3cd', borderRadius: 8, border: '1px solid #ffeaa7' }}>
            <p style={{ margin: 0, fontSize: 13, color: '#856404' }}>
              <strong>Important:</strong> These permissions enable our ad automation platform to create GPS-triggered Facebook campaigns. You can skip this step and connect Facebook later in your account settings.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Signup;
